/* ============================================================
   postProcessingView — 00a0da87 (Artem §4 2026-08-28): pure view
   model for post-processing surfaces.

   Customer toggles paint / support removal on the quote; each
   performer prices them by their own rates. Honest flags come
   from the backend (PaintPriceKnown / SupportRemovalPriced):
   unknown paint area or support mass → «уточняется в чате»,
   never a fake exact estimate.

   Pure functions; QuotePage / PerformerList / ProviderPricingPanel
   own the state.
   ============================================================ */

import type { MatchOption, MatchPostProcessing } from '../types';
import type { ProviderPostProcessingOptions } from '../types/pricing';

/* ── Match view: what one performer's card shows ──────────── */

export type MatchPostLineKind = 'applied' | 'chat' | 'not-offered' | 'option';

/** One renderable line of a match card's post-processing block. */
export interface MatchPostLine {
  kind: MatchPostLineKind;
  /** Base text, e.g. «Покраска» or the option name. */
  text: string;
  /** Right-side detail, e.g. «в цене · 12 ₽/см²» or «350 ₽/шт». */
  detail: string;
}

function num(value: number | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function formatRub(value: number | null | undefined): string {
  return num(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

/**
 * Build the post-processing lines for one match card.
 *
 * - toggled + applied → the price is already inside printPriceRub;
 * - toggled + offered but unknown → «уточняется в чате» (honest flag);
 * - toggled but not offered (rate 0) → say so, never a fake price;
 * - performer-defined options → chips with the per-copy price.
 * Returns null when there is nothing customer-visible to show.
 */
export function matchPostLines(post: MatchPostProcessing | null | undefined): MatchPostLine[] | null {
  if (!post) return null;
  const lines: MatchPostLine[] = [];

  if (post.includePaint) {
    if (post.paintApplied) {
      lines.push({
        kind: 'applied',
        text: 'Покраска',
        detail: `в цене · ${formatRub(post.paintPricePerCm2Rub)} ₽/см²`,
      });
    } else if (!post.paintPriceKnown) {
      lines.push({ kind: 'chat', text: 'Покраска', detail: 'уточняется в чате' });
    } else {
      // known && !applied && toggled → performer does not offer painting.
      lines.push({ kind: 'not-offered', text: 'Покраска', detail: 'исполнитель не предлагает' });
    }
  }

  if (post.includeSupportRemoval) {
    if (post.supportRemovalPriced && num(post.supportRemovalPricePerGramRub) > 0) {
      // Priced per support gram and already inside printPriceRub.
      // 7f5734fc: an analyzer-estimated mass is labeled «оценка» — measured
      // slicer masses (future slicing path) render without the marker.
      const grams = num(post.supportGramsUsed);
      const estimateNote = grams > 0 && post.supportGramsIsEstimate
        ? ` · ~${grams.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} г (оценка)`
        : '';
      lines.push({
        kind: 'applied',
        text: 'Удаление поддержек',
        detail: `в цене · ${formatRub(post.supportRemovalPricePerGramRub)} ₽/г${estimateNote}`,
      });
    } else if (!post.supportRemovalPriced && num(post.supportRemovalPricePerGramRub) > 0) {
      lines.push({ kind: 'chat', text: 'Удаление поддержек', detail: 'уточняется в чате' });
    } else {
      lines.push({ kind: 'not-offered', text: 'Удаление поддержек', detail: 'исполнитель не предлагает' });
    }
  }

  const options = (post.options ?? []).filter(
    (o): o is MatchOption => o != null && typeof o.name === 'string' && o.name.length > 0,
  );
  for (const o of options) {
    lines.push({ kind: 'option', text: o.name, detail: `${formatRub(o.pricePerCopyRub)} ₽/шт` });
  }

  return lines.length > 0 ? lines : null;
}

/**
 * Caption above the match block: what the customer asked for vs. what is
 * priced. Keeps the card honest when both toggles are chat-priced.
 */
export function matchPostCaption(post: MatchPostProcessing | null | undefined): string {
  if (!post) return '';
  const asked = [
    post.includePaint ? 'покраска' : null,
    post.includeSupportRemoval ? 'удаление поддержек' : null,
  ].filter((s): s is string => s != null);
  return asked.length > 0
    ? `Постобработка: ${asked.join(' + ')} — цену называет каждый исполнитель`
    : 'Постобработка у исполнителя';
}

/* ── Quote page toggles ──────────────────────────────────── */

export interface PostTogglesState {
  includePaint: boolean;
  includeSupportRemoval: boolean;
}

export const POST_TOGGLES_INITIAL: PostTogglesState = {
  includePaint: false,
  includeSupportRemoval: false,
};

/** Flip one toggle (UI never mutates the rest). */
export function togglePostProcessing(
  state: PostTogglesState,
  key: keyof PostTogglesState,
): PostTogglesState {
  return { ...state, [key]: !state[key] };
}

/* ── Cabinet editor validation (mirrors backend rules) ───── */

export const MAX_POST_OPTIONS = 20;

/**
 * Mirror of ProviderPricingService post-processing validation, so the
 * cabinet editor blocks the same payloads the API would reject:
 * rates ≥ 0; positive rate when an offer is on; ≤20 options;
 * names 1–100 chars after trim; unique names (case-insensitive);
 * option prices ≥ 0.
 */
export function validatePostProcessingOptions(
  options: ProviderPostProcessingOptions | null | undefined,
): string[] {
  if (!options) return [];
  const errors: string[] = [];
  const o = options;

  if (num(o.paintPricePerCm2Rub) < 0 || num(o.paintMinimumRub) < 0
    || num(o.supportRemovalPricePerGramRub) < 0 || num(o.supportRemovalMinimumRub) < 0) {
    errors.push('Цены постобработки должны быть неотрицательными.');
  }
  if (o.offersPainting && num(o.paintPricePerCm2Rub) <= 0) {
    errors.push('Цена покраски за см² должна быть больше нуля, если покраска включена.');
  }
  if (o.offersSupportRemoval && num(o.supportRemovalPricePerGramRub) <= 0) {
    errors.push('Цена удаления поддержек за грамм должна быть больше нуля, если услуга включена.');
  }

  const list = o.options ?? [];
  if (list.length > MAX_POST_OPTIONS) {
    errors.push(`Допускается не больше ${MAX_POST_OPTIONS} дополнительных опций.`);
  }
  const seen = new Set<string>();
  for (const opt of list) {
    const name = (opt?.name ?? '').trim();
    if (name.length < 1 || name.length > 100) {
      errors.push('Названия опций — от 1 до 100 символов.');
    }
    if (num(opt?.pricePerCopyRub) < 0) {
      errors.push('Цены опций должны быть неотрицательными.');
    }
    const key = name.toLowerCase();
    if (key.length > 0) {
      if (seen.has(key)) errors.push('Названия опций не должны повторяться.');
      seen.add(key);
    }
  }
  return errors;
}

/** The payload the cabinet sends: null when nothing is offered/configured. */
export function postProcessingPayload(
  options: ProviderPostProcessingOptions,
): ProviderPostProcessingOptions | null {
  const offersNothing = !options.offersPainting && !options.offersSupportRemoval
    && (options.options ?? []).every((o) => !o || (o.name ?? '').trim() === '');
  if (offersNothing) return null;
  return {
    ...options,
    options: (options.options ?? [])
      .map((o) => ({ name: (o.name ?? '').trim(), pricePerCopyRub: num(o.pricePerCopyRub) }))
      .filter((o) => o.name.length > 0),
  };
}
